import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { money0 } from '../lib/format';
import { ImagePicker } from './ImagePicker';
import { Empty, Loading, Modal, Select, TextInput, useToast } from './ui';

export interface RoomType {
  id: string;
  name: string;
  baseTariff: number;
  maxOccupancy: number;
  amenities: string[];
  gstRate: number;
  /** Shown to guests on the booking page. */
  imageUrl: string | null;
}

/**
 * Room inventory setup: room types first, then the rooms themselves.
 *
 * A newly registered hotel has neither, so this is the first thing an owner
 * does. Rooms are added as a range because typing 1 to 24 one at a time is not
 * a setup flow anybody finishes.
 */
export function RoomSetup({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();

  const types = useQuery({
    queryKey: ['room-types'],
    queryFn: () => api.get<RoomType[]>('/api/room-types'),
  });

  const [typeForm, setTypeForm] = useState({
    name: '',
    baseTariff: '',
    maxOccupancy: '2',
    amenities: '',
  });

  const [roomForm, setRoomForm] = useState({
    roomTypeId: '',
    floor: '1',
    prefix: '',
    from: '',
    to: '',
  });
  const [result, setResult] = useState<string | null>(null);

  const createType = useMutation({
    mutationFn: () =>
      api.post<RoomType>('/api/room-types', {
        name: typeForm.name.trim(),
        baseTariff: Number(typeForm.baseTariff),
        maxOccupancy: Number(typeForm.maxOccupancy),
        amenities: typeForm.amenities
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
      }),
    onSuccess: (created) => {
      push(`${created.name} added`, 'ok');
      setTypeForm({ name: '', baseTariff: '', maxOccupancy: '2', amenities: '' });
      setRoomForm((form) => ({ ...form, roomTypeId: form.roomTypeId || created.id }));
      void queryClient.invalidateQueries({ queryKey: ['room-types'] });
    },
    onError: fail,
  });

  /**
   * Adds each room in turn and reports what happened.
   *
   * A number that already exists comes back as a 409; that is skipped rather
   * than abandoning the whole range, because re-running the setup to fill a gap
   * is a normal thing to do.
   */
  const createRooms = useMutation({
    mutationFn: async () => {
      const from = Number(roomForm.from);
      const to = Number(roomForm.to || roomForm.from);
      const floor = Number(roomForm.floor);

      let created = 0;
      const skipped: string[] = [];
      const failed: string[] = [];

      for (let n = from; n <= to; n += 1) {
        const number = `${roomForm.prefix.trim()}${n}`;
        try {
          await api.post('/api/rooms', {
            number,
            roomTypeId: roomForm.roomTypeId,
            floor,
            status: 'VACANT',
          });
          created += 1;
        } catch (error) {
          if (error instanceof ApiError && error.status === 409) skipped.push(number);
          else failed.push(number);
        }
      }
      return { created, skipped, failed };
    },
    onSuccess: ({ created, skipped, failed }) => {
      const parts = [`${created} room${created === 1 ? '' : 's'} added`];
      if (skipped.length) parts.push(`${skipped.length} already existed (${skipped.join(', ')})`);
      if (failed.length) parts.push(`${failed.length} could not be added (${failed.join(', ')})`);
      setResult(parts.join(' · '));
      if (created > 0) push(parts[0]!, 'ok');
      setRoomForm((form) => ({ ...form, from: '', to: '' }));
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
    onError: fail,
  });

  const typeReady = typeForm.name.trim().length > 0 && Number(typeForm.baseTariff) > 0;
  const rangeFrom = Number(roomForm.from);
  const rangeTo = Number(roomForm.to || roomForm.from);
  const rangeSize =
    Number.isFinite(rangeFrom) && Number.isFinite(rangeTo) ? rangeTo - rangeFrom + 1 : 0;
  const roomsReady =
    Boolean(roomForm.roomTypeId) && roomForm.from !== '' && rangeSize > 0 && rangeSize <= 200;

  return (
    <Modal title="Set up rooms" onClose={onClose} size="wide">
      <div className="stack">
        <section className="card">
          <div className="card-head">
            <h3>Room types</h3>
            <span className="small muted">
              Tariff drives the GST slab: 12% up to ₹7,500, 18% above
            </span>
          </div>

          {types.isLoading ? (
            <Loading />
          ) : (types.data?.length ?? 0) === 0 ? (
            <Empty message="No room types yet — add your first one below" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th className="num">Tariff / night</th>
                    <th className="num">Sleeps</th>
                    <th className="num">GST</th>
                    <th>Amenities</th>
                    <th>Photo</th>
                  </tr>
                </thead>
                <tbody>
                  {types.data!.map((type) => (
                    <tr key={type.id}>
                      <td className="b">{type.name}</td>
                      <td className="num">{money0(type.baseTariff)}</td>
                      <td className="num">{type.maxOccupancy}</td>
                      <td className="num">{type.gstRate}%</td>
                      <td className="small dim">{type.amenities.join(', ') || '—'}</td>
                      <td>
                        {/* Guests book a room type, not a numbered room, so the
                            photograph belongs here. */}
                        <ImagePicker
                          endpoint={`/api/room-types/${type.id}/image`}
                          imageUrl={type.imageUrl}
                          alt={type.name}
                          canEdit
                          size="sm"
                          uploadLabel="Add photo"
                          invalidateKeys={[['room-types'], ['rooms']]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card-body">
            <div className="form-grid">
              <TextInput
                label="Name *"
                placeholder="Deluxe"
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
              />
              <TextInput
                label="Tariff per night (₹) *"
                type="number"
                min={1}
                placeholder="4500"
                value={typeForm.baseTariff}
                onChange={(e) => setTypeForm({ ...typeForm, baseTariff: e.target.value })}
              />
              <TextInput
                label="Sleeps"
                type="number"
                min={1}
                max={10}
                value={typeForm.maxOccupancy}
                onChange={(e) => setTypeForm({ ...typeForm, maxOccupancy: e.target.value })}
              />
              <TextInput
                label="Amenities"
                placeholder="AC, WiFi, TV"
                hint="Comma separated"
                value={typeForm.amenities}
                onChange={(e) => setTypeForm({ ...typeForm, amenities: e.target.value })}
              />
            </div>
            <button
              className="btn primary"
              style={{ marginTop: 12 }}
              disabled={!typeReady || createType.isPending}
              onClick={() => createType.mutate()}
            >
              {createType.isPending ? 'Adding…' : 'Add room type'}
            </button>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <h3>Add rooms</h3>
            <span className="small muted">One room, or a numbered range</span>
          </div>
          <div className="card-body">
            {(types.data?.length ?? 0) === 0 ? (
              <Empty message="Add a room type first — every room belongs to one" />
            ) : (
              <>
                <div className="form-grid">
                  <Select
                    label="Room type *"
                    value={roomForm.roomTypeId}
                    onChange={(e) => setRoomForm({ ...roomForm, roomTypeId: e.target.value })}
                  >
                    <option value="">Choose a type…</option>
                    {types.data!.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name} — {money0(type.baseTariff)}/night
                      </option>
                    ))}
                  </Select>
                  <TextInput
                    label="Floor"
                    type="number"
                    min={0}
                    max={60}
                    value={roomForm.floor}
                    onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}
                  />
                  <TextInput
                    label="Number prefix"
                    placeholder="Optional, e.g. A"
                    value={roomForm.prefix}
                    onChange={(e) => setRoomForm({ ...roomForm, prefix: e.target.value })}
                  />
                  <div />
                  <TextInput
                    label="From number *"
                    type="number"
                    placeholder="101"
                    value={roomForm.from}
                    onChange={(e) => setRoomForm({ ...roomForm, from: e.target.value })}
                  />
                  <TextInput
                    label="To number"
                    type="number"
                    placeholder="110"
                    hint="Leave blank to add a single room"
                    value={roomForm.to}
                    onChange={(e) => setRoomForm({ ...roomForm, to: e.target.value })}
                  />
                </div>

                <div className="row between wrap gap-6" style={{ marginTop: 12 }}>
                  <button
                    className="btn primary"
                    disabled={!roomsReady || createRooms.isPending}
                    onClick={() => {
                      setResult(null);
                      createRooms.mutate();
                    }}
                  >
                    {createRooms.isPending
                      ? 'Adding…'
                      : rangeSize > 1
                        ? `Add ${rangeSize} rooms`
                        : 'Add room'}
                  </button>
                  {rangeSize > 200 ? (
                    <span className="small warn">That is more than 200 rooms in one go.</span>
                  ) : null}
                </div>

                {result ? (
                  <p className="small" style={{ marginTop: 10 }}>
                    {result}
                  </p>
                ) : null}
              </>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}
